import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  HStack,
  Input,
  Select,
  useToast,
  useColorModeValue,
} from '@chakra-ui/react';
import { Check, X } from 'lucide-react';
import { Icon } from '@chakra-ui/react';
import { ActionItem, User } from '../../types';
import { useAuth } from '../../../../contexts/AuthContext';
import { formatDateForInput } from '../../../../utils/date.utils';

interface EditActionItemFormProps {
  actionItem: ActionItem;
  rockId: string;
  onUpdate: (actionItemId: string, task: string, assigneeId: string | null, dueDate: string) => Promise<void>;
  onSuccess?: () => void;
  onCancel?: () => void;
  users?: User[];
}

/**
 * Inline form for editing an existing action item
 * Uses Formik for form state and Yup for validation
 */
export function EditActionItemForm({
  actionItem,
  rockId,
  onUpdate,
  onSuccess,
  onCancel,
  users,
}: EditActionItemFormProps) {
  const { eosUsers = [] } = useAuth();
  const toast = useToast();

  // Color mode values for dark mode support
  const formBg = useColorModeValue('green.50', 'green.900');
  const inputBg = useColorModeValue('white', 'gray.700');

  const validationSchema = Yup.object({
    task: Yup.string()
      .required('Task is required')
      .min(3, 'Task must be at least 3 characters'),
    assigneeId: Yup.string().nullable(),
    dueDate: Yup.string().required('Due date is required'),
  });

  const handleSubmit = async (
    values: { task: string; assigneeId: string; dueDate: string },
    { setSubmitting }: any
  ) => {
    try {
      // Format due date: append T12:00:00 to avoid timezone shift
      const formattedDueDate = values.dueDate.includes('T') 
        ? values.dueDate 
        : `${values.dueDate}T12:00:00`;
      
      await onUpdate(actionItem.id, values.task, values.assigneeId || null, formattedDueDate);
      toast({
        title: 'Action item updated',
        status: 'success',
        duration: 2000,
        isClosable: true,
      });
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update action item',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Get the current assignee ID
  const currentAssigneeId = actionItem.assignedToUserId || actionItem.assigneeId || actionItem.ownerId || '';

  return (
    <Box p={3} bg={formBg} borderRadius="md" mb={2}>
      <Formik
        initialValues={{
          task: actionItem.description || actionItem.task || '',
          assigneeId: currentAssigneeId,
          dueDate: formatDateForInput(actionItem.dueDate),
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <HStack spacing={2} align="flex-start" wrap="wrap">
              {onCancel && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={onCancel}
                  aria-label="Cancel"
                  isDisabled={isSubmitting}
                >
                  <Icon as={X} w={4} h={4} />
                </Button>
              )}
              <Button
                type="submit"
                size="sm"
                variant="ghost"
                colorScheme="green"
                isLoading={isSubmitting}
                aria-label="Save"
              >
                <Icon as={Check} w={4} h={4} />
              </Button>

              <Field name="task">
                {({ field, form }: any) => (
                  <FormControl isInvalid={form.errors.task && form.touched.task} flex={2} minW="200px">
                    <Input
                      {...field}
                      placeholder="To-do description"
                      size="sm"
                      bg={inputBg}
                    />
                    <FormErrorMessage fontSize="xs">{form.errors.task}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>

              <Field name="assigneeId">
                {({ field, form }: any) => (
                  <FormControl isInvalid={form.errors.assigneeId && form.touched.assigneeId} flex={1} minW="150px">
                    <Select {...field} placeholder="Assign to (optional)" size="sm" bg={inputBg}>
                      {eosUsers.map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.name}
                        </option>
                      ))}
                    </Select>
                    <FormErrorMessage fontSize="xs">{form.errors.assigneeId}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>

              <Field name="dueDate">
                {({ field, form }: any) => (
                  <FormControl isInvalid={form.errors.dueDate && form.touched.dueDate} flex={1} minW="150px">
                    <Input
                      {...field}
                      type="date"
                      size="sm"
                      bg={inputBg}
                    />
                    <FormErrorMessage fontSize="xs">{form.errors.dueDate}</FormErrorMessage>
                  </FormControl>
                )}
              </Field>
            </HStack>
          </Form>
        )}
      </Formik>
    </Box>
  );
}
